import {useLocation, useNavigate, useParams} from "react-router-dom";
import {
    DeviceRdlAuthenticationStatus,
    IdListRequest,
    useDeviceGetQuery,
    useDeviceHistoryListQuery,
    useDeviceRdlAuthenticateMutation,
    useDeviceRdlAuthenticationStatusQuery,
    useDeviceRestartFirmwareUpdateMutation,
    useDevicesCommandMutation,
    useOrganizationStageListQuery,
} from "../../app/api/api";
import QueryErrorAlert from "../../components/QueryErrorAlert";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import DeviceFwUpdateDialog from "./components/DeviceFirmwareUpdateDialog";
import Page, {ContentTitle, TitleBarButton} from "../../components/Layout/Page";
import TabSection from "../../components/Layout/Page/TabSection";
import DeviceGeneralTab from "./generalTab/DeviceGeneralTab";
import {DeviceCommand, DeviceHistoryEntry} from "../../app/api/model/deviceHistory";
import HistoryTable from "./historyTable/HistoryTable";
import {DeviceField} from "../../app/api/model/device";
import {Firmware, FirmwareField} from "../../app/api/model/firmware";
import {usePermission} from "../../hooks/usePermission";
import {PermissionLevel} from "../../app/api/model/user";
import {ReactComponent as RemoteDownloadIcon} from "../../assets/icons/remote_download.svg";
import {ReactComponent as DownloadIcon} from "../../assets/icons/download.svg";
import {ReactComponent as DriverCardDownloadIcon} from "../../assets/icons/driverCardDownload.svg";
import {Colors} from "../../components/Layout/Colors";
import {ResponseErrorAlert} from "../../components/ResponseErrorMessages";
import {useSnackbar} from "notistack";
import UpdateIcon from "@mui/icons-material/Update";
import UpdateDisabledIcon from "@mui/icons-material/UpdateDisabled";
import {ReactComponent as RestartUpdateIcon} from "../../assets/icons/reset.svg";
import {useCancelFirmwareUpdate} from "../../hooks/useCancelFirmwareUpdate";
import DeviceMassStorageDownloadDialog from "./components/DeviceMassStorageDownloadDialog";
import {useModal} from "../../hooks/useModal";
import DeviceDriverCardDownloadDialog from "./components/DeviceDriverCardDownloadDialog";
import {OrganizationStage, StageField, StageIdentifier} from "../../app/api/model/stage";
import {OrganizationField} from "../../app/api/model/organization";
import {skipToken} from "@reduxjs/toolkit/query/react";
import {Cached, MyLocation} from "@mui/icons-material";

function hasAnyNonLegacyStage(organizationStages: OrganizationStage[]): boolean {
    return organizationStages.some(
        (organizationStage: OrganizationStage) => organizationStage[StageField.IDENTIFIER] !== StageIdentifier.LEGACY
    );
}

const DEVICE_JOBS_TAB_INDEX = 5;

export default function DeviceDetails() {
    const params = useParams();
    const deviceId = parseInt(params.deviceId ?? "");
    const {hasPermission, hasExactPermission} = usePermission();
    const {data: device, error} = useDeviceGetQuery(deviceId);
    const {data: deviceAuthStatus} = useDeviceRdlAuthenticationStatusQuery(deviceId, {
        skip: !hasPermission(PermissionLevel.MANAGER),
        pollingInterval: 5000,
    });
    const organisation = device?.[DeviceField.ORGANIZATION];
    const organisationId = organisation?.[OrganizationField.ID];
    const organizationStagesResponse = useOrganizationStageListQuery(
        organisationId == null ? skipToken : {id: organisationId}
    );
    const noDKVLive2Environment = !hasAnyNonLegacyStage(organizationStagesResponse?.currentData?.payload ?? []);
    const [deviceRdlAuthenticate] = useDeviceRdlAuthenticateMutation();
    const [devicesCommand] = useDevicesCommandMutation();
    const [deviceFirmwareUpdate] = useDeviceRestartFirmwareUpdateMutation();
    const [isFwUpdateDialogOpen, setIsFwUpdateDialogOpen] = useState(false);
    const [isMassStorageDialogOpen, setIsMassStorageDialogOpen] = useState(false);
    const deviceMassStorageDownloadModal = useModal(false);
    const deviceDriverCardDownloadModal = useModal(false);
    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();
    const {isActive: isCancelFirmwareButtonActive, cancelFirmwareUpdate} = useCancelFirmwareUpdate({
        devices: [device],
    });
    const navigate = useNavigate();
    const {hash} = useLocation();

    const deviceJobsUrl = organisationId ? `/organizations/${organisationId}#${DEVICE_JOBS_TAB_INDEX}` : null;

    if (error) {
        return <QueryErrorAlert error={error} />;
    }

    const handleRdlAuthentication = async () => {
        try {
            await deviceRdlAuthenticate(deviceId).unwrap();
            enqueueSnackbar(t("device_rdlAuthenticationSuccess"), {variant: "success"});
        } catch {
            // RTK Query's rtkQueryErrorLogger middleware displays the authentication error.
        }
    };

    const getAuthStatusTooltipMessage = (status?: DeviceRdlAuthenticationStatus) => {
        if (status === DeviceRdlAuthenticationStatus.IN_PROGRESS) {
            return t("device_rdlAuthenticationInProgress");
        } else if (status === DeviceRdlAuthenticationStatus.RDL_NOT_ACTIVE) {
            return t("device_rdlAuthenticationNot");
        } else {
            return "";
        }
    };

    const handleRequestLocation = async () => {
        try {
            await devicesCommand({deviceIds: [deviceId], command: DeviceCommand.REQUEST_LOCATION})
                .unwrap()
                .then(() => {
                    enqueueSnackbar(t("device_smsCommandRequestSend"), {variant: "success"});
                });
        } catch (authError) {
            enqueueSnackbar(
                <ResponseErrorAlert title={t("generic_errorResponseTitle")} body={t("generic_errorResponseBody")} />,
                {
                    variant: "error",
                }
            );
        }
    };

    const handleDeviceReboot = async () => {
        try {
            await devicesCommand({deviceIds: [deviceId], command: DeviceCommand.RESET})
                .unwrap()
                .then(() => {
                    enqueueSnackbar(t("device_smsCommandRequestSend"), {variant: "success"});
                });
        } catch (authError) {
            enqueueSnackbar(
                <ResponseErrorAlert title={t("generic_errorResponseTitle")} body={t("generic_errorResponseBody")} />,
                {
                    variant: "error",
                }
            );
        }
    };

    const handleRestartFirmwareUpdate = async () => {
        try {
            await deviceFirmwareUpdate({deviceIds: [deviceId]}).unwrap();
        } catch (authError) {
            enqueueSnackbar(
                <ResponseErrorAlert title={t("generic_errorResponseTitle")} body={t("generic_errorResponseBody")} />,
                {
                    variant: "error",
                }
            );
        }
    };

    const requestLocationButton: TitleBarButton = {
        key: "requestLocation",
        tooltip: "action_requestLocation",
        disabled: false,
        minRequiredPermissionToSee: PermissionLevel.SUPPORT,
        callback: () => handleRequestLocation(),
        icon: <MyLocation />,
    };

    const rebootDeviceButton: TitleBarButton = {
        key: "rebootDevice",
        tooltip: "action_rebootDevice",
        disabled: false,
        minRequiredPermissionToSee: PermissionLevel.SUPPORT,
        callback: () => handleDeviceReboot(),
        icon: <Cached />,
    };

    const restartFirmwareUpdateButton: TitleBarButton = {
        key: "restartFirmwareUpdate",
        tooltip: "action_restartFirmwareUpdate",
        disabled: false,
        minRequiredPermissionToSee: PermissionLevel.SUPPORT,
        callback: () => handleRestartFirmwareUpdate(),
        icon: <RestartUpdateIcon />,
    };

    const supportButtons: TitleBarButton[] = [requestLocationButton, rebootDeviceButton, restartFirmwareUpdateButton];

    const managerButtons: TitleBarButton[] = [
        requestLocationButton,
        rebootDeviceButton,
        {
            key: "updateFirmware",
            tooltip: "action_updateFirmware",
            disabled: isFwUpdateDialogOpen,
            minRequiredPermissionToSee: PermissionLevel.MANAGER,
            callback: () => setIsFwUpdateDialogOpen(true),
            icon: <UpdateIcon />,
        },
        restartFirmwareUpdateButton,
        {
            key: "cancelFirmwareUpdate",
            tooltip: "action_cancelFirmwareUpdate",
            disabled: !isCancelFirmwareButtonActive,
            minRequiredPermissionToSee: PermissionLevel.MANAGER,
            callback: cancelFirmwareUpdate,
            icon: <UpdateDisabledIcon />,
        },
        {
            key: "manuallyAuthenticate",
            tooltip: "action_manuallyAuthentication",
            tooltipDisabled: getAuthStatusTooltipMessage(deviceAuthStatus?.status),
            disabled:
                noDKVLive2Environment ||
                !device?.[DeviceField.ORGANIZATION] ||
                deviceAuthStatus?.status === DeviceRdlAuthenticationStatus.IN_PROGRESS ||
                device?.[DeviceField.ONLINE] !== true,
            minRequiredPermissionToSee: PermissionLevel.MANAGER,
            callback: () => {
                handleRdlAuthentication();
            },
            icon: <RemoteDownloadIcon fill={Colors.GRAY_115} />,
        },
        {
            key: "manuallyMassDownload",
            tooltip: "action_manualMassStorageDownload",
            tooltipDisabled: getAuthStatusTooltipMessage(deviceAuthStatus?.status),
            disabled: noDKVLive2Environment || device?.[DeviceField.ONLINE] !== true,
            minRequiredPermissionToSee: PermissionLevel.MANAGER,
            callback: () => setIsMassStorageDialogOpen(true),
            icon: <DownloadIcon fill={Colors.GRAY_115} />,
        },
        {
            key: "driverCardDownload",
            tooltip: "action_manualDriverCardDownload",
            disabled: noDKVLive2Environment || device?.[DeviceField.ONLINE] !== true,
            callback: () => deviceDriverCardDownloadModal.setIsOpen(true),
            minRequiredPermissionToSee: PermissionLevel.MANAGER,
            icon: <DriverCardDownloadIcon fill={Colors.GRAY_115} />,
        },
    ];

    function getButtonsForPermission() {
        if (hasExactPermission(PermissionLevel.VIEW_ONLY)) {
            return undefined;
        } else if (hasExactPermission(PermissionLevel.SUPPORT)) {
            return supportButtons;
        }

        return hasPermission(PermissionLevel.MANAGER) ? managerButtons : undefined;
    }

    return (
        <Page>
            <ContentTitle
                titleKey="generic_device"
                subtitle={device?.[DeviceField.MSISDN]}
                buttons={getButtonsForPermission()}
                entry={device}
                toolEntries={[
                    {
                        translationKey: "device_currentFirmwareLegacy",
                        fieldName: DeviceField.CURRENT_FIRMWARE,
                        render: (curFirmware?: Firmware) =>
                            curFirmware?.[FirmwareField.LEGACY] ? t("generic_yes") : t("generic_no"),
                    },
                ]}
            />
            <TabSection
                title={t("generic_devices")}
                onChange={(event, newValue) => {
                    navigate(`#${newValue}`, {replace: true});
                }}
                tabs={[
                    {label: t("generic_general"), content: device && <DeviceGeneralTab device={device} />},
                    {
                        label: t("generic_history"),
                        content: device && (
                            <HistoryTable<DeviceHistoryEntry, IdListRequest>
                                additionalRequestParams={{id: device.id}}
                                queryHook={useDeviceHistoryListQuery}
                            />
                        ),
                    },
                ]}
                initialTab={Number(hash.at(1)) || 0}
                navigationTabs={deviceJobsUrl ? [{label: t("organizations_deviceJobList"), to: deviceJobsUrl}] : []}
            />
            <DeviceFwUpdateDialog
                open={isFwUpdateDialogOpen}
                onClose={() => setIsFwUpdateDialogOpen(false)}
                deviceIds={device ? [device[DeviceField.ID]] : []}
                hardwareType={device?.[DeviceField.HARDWARE_TYPE]}
                defaultFirmwareId={device?.[DeviceField.CURRENT_FIRMWARE]?.[FirmwareField.ID]}
            />
            <DeviceMassStorageDownloadDialog
                open={isMassStorageDialogOpen}
                onClose={() => setIsMassStorageDialogOpen(false)}
                deviceId={deviceId}
                modal={deviceMassStorageDownloadModal}
                organizationId={device?.[DeviceField.ORGANIZATION]?.id}
            />
            {device?.[DeviceField.ORGANIZATION] && (
                <DeviceDriverCardDownloadDialog
                    open={deviceDriverCardDownloadModal.isOpen}
                    onClose={() => deviceDriverCardDownloadModal.setIsOpen(false)}
                    deviceId={deviceId}
                    organizationId={device[DeviceField.ORGANIZATION]!.id}
                />
            )}
        </Page>
    );
}
